import { createClient } from '@supabase/supabase-js';

// Get environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Validate environment variables
if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables. Please check your .env file.');
}

// Create Supabase client with retries and error handling
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    flowType: 'pkce',
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true
  },
  global: {
    headers: {
      'x-application-name': 'payflow'
    }
  },
  storage: {
    retryAttempts: 3,
    retryInterval: 1000
  }
});

// Google OAuth configuration
export const signInWithGoogle = async () => {
  try {
    // Import the new startGoogleLogin function
    const { startGoogleLogin } = await import('../features/auth/startGoogleLogin');
    return await startGoogleLogin();
  } catch (error) {
    console.error('Error with new Google login, falling back to old method:', error);
    
    // Fallback to original method if new one fails
    const { data, error: authError } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    
    if (authError) throw authError;
    return data;
  }
};

// Add error handling for failed connections
supabase.auth.onAuthStateChange((event, session) => {
  if (event === 'SIGNED_OUT') {
    // Clear any cached data
    localStorage.removeItem('user');
    sessionStorage.removeItem('user');
  }
});

// Helper function to handle Supabase errors
export const handleSupabaseError = (error: any) => {
  console.error('Supabase error:', error);
  
  if (error.code === 'PGRST116') {
    return 'No data found';
  }
  
  if (error.code === '23505') {
    return 'This record already exists';
  }
  
  if (error.code === '42501') {
    return 'You do not have permission to perform this action';
  }
  
  if (error.code === '404') {
    return 'Resource not found';
  }
  
  if (error.message) {
    return error.message;
  }
  
  return 'An unexpected error occurred';
};

// Helper function to check if Supabase is connected
export const checkSupabaseConnection = async () => {
  try {
    const { data, error } = await supabase.from('stripeusers').select('count').single();
    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Failed to connect to Supabase:', error);
    return false;
  }
};

// Helper function to check if storage is available
export const checkStorageAvailability = async () => {
  try {
    const { data, error } = await supabase.storage.getBucket('public');
    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Failed to access storage:', error);
    return false;
  }
};